
import React, { useEffect, useState, useContext, createContext, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { AuthState, User, CertificationType } from '../types';
import { Loader2, Lock, Zap, Mail, Key, AlertTriangle, LogIn, X, LogOut, ChevronDown, ShieldCheck, Laptop, Smartphone, Check } from 'lucide-react';
import { StorageService } from '../services/storageService';
import { GuestTrialService } from '../services/guestTrialService';
import { Footer } from './Footer';
import { SponsoredService } from '../services/sponsoredService';
import { useThemeTokens } from '../hooks/useActiveTheme';
import { sanitizeAndExtractUrlToken } from '../services/authSanitizer';

interface AuthContextType extends AuthState {
  logout: () => void;
  login: (email: string, password?: string) => Promise<any>;
  verify2FA: (preAuthToken: string, code: string, trustDevice?: boolean) => Promise<any>;
  // Updated return type to Promise<void> to support .catch() in Walkthrough.tsx
  updateUser: (data: Partial<User>) => Promise<void>;
  switchCert: (certId: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isBlocked: false,
    token: null,
    user: null,
    activeCert: null,
    isLoading: true,
    error: null,
  });

  const tokens = useThemeTokens();
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const [showPasswordAuth, setShowPasswordAuth] = useState(false);
  const [reAuthEmail, setReAuthEmail] = useState('');
  const [reAuthPassword, setReAuthPassword] = useState('');
  const [reAuth2FAChallenge, setReAuth2FAChallenge] = useState<{
    preAuthToken: string;
    user: any;
  } | null>(null);
  const [reAuth2FACode, setReAuth2FACode] = useState('');
  const [reAuthTrustDevice, setReAuthTrustDevice] = useState(true);
  const [reAuthUsingBackup, setReAuthUsingBackup] = useState(false);
  const [reAuthError, setReAuthError] = useState<string | null>(null);
  const [isWixReAuthing, setIsWixReAuthing] = useState(false);
  const [isReAuthLoggingIn, setIsReAuthLoggingIn] = useState(false);

  // Keep reAuthEmail synchronized with logged in user
  useEffect(() => {
    if (state.user?.email) {
      setReAuthEmail(state.user.email);
    }
  }, [state.user?.email]);

  // Support URL query parameter testing: ?test_session_expired=true or ?session_expired=1
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('test_session_expired') === 'true' || params.get('session_expired') === '1') {
      setIsSessionExpired(true);
    }
  }, []);

  // Inactivity / Idle Timeout detection (30 minutes of no interaction)
  useEffect(() => {
    if (!state.isAuthenticated) return;

    let timeoutId: any = null;
    const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

    const resetIdleTimer = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        console.warn('UI session timed out due to inactivity.');
        setIsSessionExpired(true);
      }, IDLE_TIMEOUT_MS);
    };

    const activityEvents = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    activityEvents.forEach(evt => window.addEventListener(evt, resetIdleTimer, { passive: true }));

    resetIdleTimer();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      activityEvents.forEach(evt => window.removeEventListener(evt, resetIdleTimer));
    };
  }, [state.isAuthenticated]);

  // Global fetch interceptor to capture 401/403 response statuses (expired session tokens)
  useEffect(() => {
    const originalFetch = window.fetch;
    const interceptedFetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      let response: Response;
      try {
        response = await originalFetch(input, init);
      } catch (err) {
        // Log network failures gracefully and rethrow to calling service
        console.warn('Fetch request encountered a network or connection error:', err);
        throw err;
      }

      if (response && response.status === 401) {
        const urlString = typeof input === 'string' 
          ? input 
          : (input instanceof URL ? input.toString() : input.url);
        
        // Exclude authentication attempts (login / register) where 401 means "invalid credentials"
        const isAuthAttempt = urlString.includes('/users/login') || urlString.includes('/users/register');
        // Exclude admin data routes to prevent admin logout during save operations
        const isAdminDataRoute = urlString.includes('/certifications') || urlString.includes('/exam-configs') || urlString.includes('/admin/seo');
        const hasSavedToken = !!window.localStorage.getItem('wix_auth_token');

        if (!isAuthAttempt && !isAdminDataRoute && (hasSavedToken || state.isAuthenticated)) {
          setIsSessionExpired(true);
        }
      }
      return response;
    };

    window.fetch = interceptedFetch;

    // Attach simulation helper for manual testing and verification via browser dev console
    (window as any).triggerSessionExpired = () => {
      setIsSessionExpired(true);
    };

    return () => {
      window.fetch = originalFetch;
      delete (window as any).triggerSessionExpired;
    };
  }, [state.isAuthenticated]);

  const handleWixReAuth = () => {
    setIsWixReAuthing(true);
    const currentFullUrl = window.location.href;
    const loginRedirectUrl = `https://www.diamondnursingschool.com/studyhub-login?redirect=${encodeURIComponent(currentFullUrl)}`;
    window.location.href = loginRedirectUrl;
  };

  const handleReAuthSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailToUse = reAuthEmail || state.user?.email;
    if (!emailToUse) {
      setReAuthError("Please enter your account email.");
      return;
    }
    setIsReAuthLoggingIn(true);
    setReAuthError(null);
    try {
      const res = await login(emailToUse, reAuthPassword);
      if (res && res.require2FA) {
        setReAuth2FAChallenge({ preAuthToken: res.preAuthToken, user: res.user });
        return;
      }
      setIsSessionExpired(false);
      setReAuthPassword('');
      setShowPasswordAuth(false);
    } catch (err: any) {
      setShowPasswordAuth(true);
      const raw = err?.message || String(err || '');
      let cleanMsg = 'Authentication verification failed. Please verify your email and password / access key.';
      if (raw.toLowerCase().includes('database') || raw.toLowerCase().includes('buffercommands') || raw.toLowerCase().includes('initializing')) {
        cleanMsg = 'Database connection is initializing. Please wait a few seconds and try again.';
      } else if (raw.toLowerCase().includes('password') || raw.toLowerCase().includes('credential') || raw.toLowerCase().includes('incorrect')) {
        cleanMsg = 'Invalid credentials. Please verify your email and password / access key.';
      } else if (raw.toLowerCase().includes('network') || raw.toLowerCase().includes('fetch') || raw.toLowerCase().includes('failed to fetch')) {
        cleanMsg = 'Unable to reach the study registry. Please check your internet connection.';
      } else if (raw.toLowerCase().includes('blocked') || raw.toLowerCase().includes('suspended')) {
        cleanMsg = 'Your account access is currently restricted. Please contact nursing faculty advising.';
      }
      setReAuthError(cleanMsg);
    } finally {
      setIsReAuthLoggingIn(false);
    }
  };

  const handleReAuth2FASubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reAuth2FAChallenge || !reAuth2FACode) return;
    setIsReAuthLoggingIn(true);
    setReAuthError(null);
    try {
      await verify2FA(reAuth2FAChallenge.preAuthToken, reAuth2FACode.trim(), reAuthTrustDevice);
      setIsSessionExpired(false);
      setReAuth2FAChallenge(null);
      setReAuth2FACode('');
      setReAuthPassword('');
      setShowPasswordAuth(false);
    } catch (err: any) {
      const raw = err?.message || String(err || '');
      let cleanMsg = 'Invalid verification code. Please check your authenticator code and try again.';
      if (raw.toLowerCase().includes('expired')) {
        cleanMsg = 'Verification code has expired. Please enter the latest code from your authenticator app.';
      } else if (raw.toLowerCase().includes('network') || raw.toLowerCase().includes('fetch')) {
        cleanMsg = 'Network connection issue. Please check your connection and retry.';
      }
      setReAuthError(cleanMsg);
    } finally {
      setIsReAuthLoggingIn(false);
    }
  };

  const loadCertification = async (certId: string) => {
    try {
        const allCerts = await StorageService.getCertifications();
        let cert = allCerts.find(c => c.id === certId.toLowerCase());
        if (!cert && allCerts.length > 0) cert = allCerts[0];
        return cert || null;
    } catch (e) {
        return null;
    }
  };

  const handleIdentitySync = async (token: string) => {
    window.localStorage.setItem('wix_auth_token', token);
    try {
        const res = await fetch('/practice-exams/api/users/me', {
            headers: { 'Authorization': `Bearer ${token}` }
        });

        if (res.ok) {
            const userRecord = await res.json();
            const cert = await loadCertification(userRecord.activeCertId || 'cna');
            
            // If the user has administrative privileges, preserve a clean session token with role in localStorage
            const isUserAdmin = userRecord.role === 'admin' || userRecord.role === 'superadmin' || userRecord.role === 'super_admin' || userRecord.role === 'staff';
            let activeSessionToken = token;
            if (isUserAdmin) {
                try {
                    activeSessionToken = btoa(JSON.stringify({
                        sub: userRecord.id || userRecord._id,
                        email: userRecord.email,
                        role: userRecord.role,
                        name: userRecord.name,
                        permissions: userRecord.permissions || ['*']
                    }));
                    window.localStorage.setItem('wix_auth_token', activeSessionToken);
                } catch (e) {}
            }

            setState({
                isAuthenticated: true,
                isBlocked: !!userRecord.isBlocked,
                token: activeSessionToken,
                user: userRecord,
                activeCert: cert,
                isLoading: false,
                error: null,
            });

            // Mark guest conversion if guest trial session exists
            try {
              GuestTrialService.markConverted(userRecord.id || userRecord._id, userRecord.email);
              StorageService.trackUserEvent({
                action: 'guest_converted',
                category: 'conversion',
                label: `User converted: ${userRecord.email}`,
                metadata: {
                  userId: userRecord.id || userRecord._id,
                  email: userRecord.email,
                  source: 'identity_sync'
                }
              });
            } catch (convErr) {
              console.warn("Conversion tracking sync warning:", convErr);
            }

            // Only redirect to dashboard if explicitly navigating to legacy hash root #/
            if (window.location.hash === '#/') {
                if (userRecord.role === 'admin') {
                    navigate('/manage/admin-dashboard', { replace: true });
                } else {
                    navigate(`/practice/${cert?.id || 'cna'}/dashboard`, { replace: true });
                }
            }
        } else {
            throw new Error("Registry validation failed");
        }
    } catch (e) {
        console.error("Identity Handoff Failure:", e);
        window.localStorage.removeItem('wix_auth_token');
        let hashPath = window.location.hash.replace(/^#\/?/, '').split('?')[0];
        let pathParts = hashPath ? hashPath.split('/').filter(Boolean) : [];
        if (pathParts.length === 0) {
          const cleanPathname = window.location.pathname.replace(/^\/(practice-exams|practice)\/?/, '').replace(/^\//, '');
          pathParts = cleanPathname ? cleanPathname.split('/').filter(Boolean) : [];
        }
        const firstSeg = pathParts[0] ? pathParts[0].toLowerCase() : '';
        const potentialCertId = (firstSeg && firstSeg !== 'manage' && firstSeg !== 'faq' && firstSeg !== '404' && firstSeg !== 'study-quiz' && firstSeg !== 'practice-exams' && firstSeg !== 'practice')
          ? firstSeg
          : 'cna';
        const defaultCert = await loadCertification(potentialCertId);
        setState(prev => ({ ...prev, isLoading: false, isAuthenticated: false, activeCert: defaultCert || null }));
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
        // Sanitize URL and extract token if arriving with access_token or token in query/hash
        const urlToken = sanitizeAndExtractUrlToken();
        const activeToken = urlToken || window.localStorage.getItem('wix_auth_token');

        if (activeToken) {
            await handleIdentitySync(activeToken);
        } else {
            // Priority 2: No valid session - hydrate default activeCert for guest exploration
            let hashPath = window.location.hash.replace(/^#\/?/, '').split('?')[0];
            let pathParts = hashPath ? hashPath.split('/').filter(Boolean) : [];
            if (pathParts.length === 0) {
              const cleanPathname = window.location.pathname.replace(/^\/(practice-exams|practice)\/?/, '').replace(/^\//, '');
              pathParts = cleanPathname ? cleanPathname.split('/').filter(Boolean) : [];
            }
            const firstSeg = pathParts[0] ? pathParts[0].toLowerCase() : '';
            const potentialCertId = (firstSeg && firstSeg !== 'manage' && firstSeg !== 'faq' && firstSeg !== '404' && firstSeg !== 'study-quiz' && firstSeg !== 'practice-exams' && firstSeg !== 'practice')
              ? firstSeg
              : 'cna';
            const defaultCert = await loadCertification(potentialCertId);
            setState(prev => ({ ...prev, isLoading: false, isAuthenticated: false, activeCert: defaultCert || null }));
        }
    };

    initializeAuth();
  }, []);

  const login = async (email: string, password?: string) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = await StorageService.loginUser(email, password);
      
      // If 2FA challenge is required for super admin / admin
      if (response && response.require2FA) {
        setState(prev => ({ ...prev, isLoading: false, error: null }));
        return response;
      }

      const userRecord = response;
      const mockToken = btoa(JSON.stringify({ sub: userRecord.id, email: userRecord.email, role: userRecord.role }));
      window.localStorage.setItem('wix_auth_token', mockToken);
      
      const cert = await loadCertification(userRecord.activeCertId || 'cna');
      
      // Mark guest conversion if guest trial session exists
      try {
        GuestTrialService.markConverted(userRecord.id || userRecord._id, userRecord.email);
        StorageService.trackUserEvent({
          action: 'guest_converted',
          category: 'conversion',
          label: `User converted via login: ${userRecord.email}`,
          metadata: {
            userId: userRecord.id || userRecord._id,
            email: userRecord.email,
            source: 'login'
          }
        });
      } catch (convErr) {
        console.warn("Conversion tracking login warning:", convErr);
      }

      setState({
          isAuthenticated: true,
          isBlocked: !!userRecord.isBlocked,
          token: mockToken,
          user: userRecord,
          activeCert: cert,
          isLoading: false,
          error: null
      });

      if (userRecord.role === 'admin') {
          navigate('/manage/admin-dashboard');
      } else {
          navigate(`/${cert?.id || 'cna'}/dashboard`);
      }
      return userRecord;
    } catch (err: any) {
      const raw = err?.message || String(err || '');
      let cleanMsg = 'Invalid email or password / access key. Please verify credentials.';
      if (raw.toLowerCase().includes('database') || raw.toLowerCase().includes('buffercommands') || raw.toLowerCase().includes('initializing')) {
        cleanMsg = 'Database connection is initializing. Please wait a few seconds and try again.';
      } else if (raw.toLowerCase().includes('network') || raw.toLowerCase().includes('fetch')) {
        cleanMsg = 'Unable to reach the registry server. Please check your internet connection.';
      } else if (raw.toLowerCase().includes('blocked') || raw.toLowerCase().includes('suspended')) {
        cleanMsg = 'This account has been suspended or restricted. Please contact nursing administration.';
      }
      setState(prev => ({ ...prev, isLoading: false, error: cleanMsg }));
      throw new Error(cleanMsg);
    }
  };

  const verify2FA = async (preAuthToken: string, code: string, trustDevice: boolean = true) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      const res = await StorageService.verify2FA({ preAuthToken, code, trustDevice });
      const userRecord = res.user;
      const mockToken = res.token;
      
      const cert = await loadCertification(userRecord.activeCertId || 'cna');
      setState({
        isAuthenticated: true,
        isBlocked: !!userRecord.isBlocked,
        token: mockToken,
        user: userRecord,
        activeCert: cert,
        isLoading: false,
        error: null
      });

      if (userRecord.role === 'admin') {
        navigate('/manage/admin-dashboard');
      } else {
        navigate(`/${cert?.id || 'cna'}/dashboard`);
      }
      return res;
    } catch (err: any) {
      const raw = err?.message || String(err || '');
      let cleanMsg = 'Invalid verification code. Please check your authenticator code and try again.';
      if (raw.toLowerCase().includes('expired')) {
        cleanMsg = 'Verification code has expired. Please enter the current code from your authenticator app.';
      } else if (raw.toLowerCase().includes('network') || raw.toLowerCase().includes('fetch')) {
        cleanMsg = 'Network connection issue. Please check your connection and retry.';
      }
      setState(prev => ({ ...prev, isLoading: false, error: cleanMsg }));
      throw new Error(cleanMsg);
    }
  };

  const switchCert = useCallback(async (certId: string, shouldRedirect: boolean = false) => {
      const cert = await loadCertification(certId);
      if (cert) {
          if (state.user) {
              const updatedUser = await StorageService.saveUser({ ...state.user, activeCertId: certId });
              setState(prev => ({ ...prev, user: updatedUser, activeCert: cert }));
          } else {
              setState(prev => ({ ...prev, activeCert: cert }));
          }
          if (shouldRedirect) {
              navigate(`/practice/${certId}/dashboard`);
          }
      }
  }, [state.user, navigate]);

  const updateUser = async (data: Partial<User>) => {
    if (state.user) {
        const updated = await StorageService.saveUser({ ...state.user, ...data });
        setState(prev => ({ ...prev, user: updated }));
    }
  };

  const logout = async () => {
    window.localStorage.removeItem('wix_auth_token');
    setState({ isAuthenticated: false, isBlocked: false, token: null, user: null, activeCert: null, isLoading: false, error: null });
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider value={{ ...state, logout, login, verify2FA, updateUser, switchCert }}>
      {children}
      
      {isSessionExpired && (
        <div id="session-expired-overlay" className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-fade-in text-slate-900 dark:text-white">
          <div className={`relative overflow-hidden flex flex-col space-y-6 max-w-md w-full p-6 md:p-8 rounded-3xl md:rounded-[2.5rem] shadow-2xl transition-all duration-300 border ${tokens.cardBg} ${tokens.headerFont}`}>
            
            {/* Ambient Accent Glow */}
            <div className="absolute top-0 right-0 w-full h-full bg-[radial-gradient(circle_at_top_right,rgba(99,102,241,0.12)_0%,transparent_50%)] pointer-events-none"></div>
            
            {/* Header Area */}
            <div className="flex flex-col items-center text-center space-y-3 relative z-10">
              <div className={`p-4 ${tokens.iconBg} rounded-3xl animate-pulse ring-8 ring-indigo-500/10 dark:ring-indigo-500/20 shadow-lg`}>
                <Lock size={28} />
              </div>
              <div className="space-y-1">
                <h2 className="text-2xl font-black tracking-tight uppercase text-slate-900 dark:text-white">Study Session Paused</h2>
                <span className={`inline-block px-3 py-1 text-[9px] font-black uppercase tracking-widest ${tokens.badge}`}>
                  Secure Registry Access Expired
                </span>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-300 font-semibold leading-relaxed max-w-sm">
                Your secure clinical study session has expired. To preserve your active progress tracking, answered questions, and study hours, please re-authenticate to continue.
              </p>
            </div>

            {/* Error Message if re-auth failed */}
            {reAuthError && (
              <div className="p-3.5 bg-rose-500/10 border border-rose-500/20 rounded-2xl flex items-center gap-2.5 text-rose-600 dark:text-rose-400 animate-pop">
                <AlertTriangle size={18} className="shrink-0" />
                <p className="text-[11px] font-bold leading-tight">{reAuthError}</p>
              </div>
            )}

            {/* Re-auth Controls */}
            <div className="space-y-4 relative z-10">
              
              {/* FIRST OPTION: Re-authenticate via School Portal */}
              <button 
                onClick={handleWixReAuth}
                disabled={isWixReAuthing}
                className={`w-full py-4 text-xs font-black tracking-widest uppercase shadow-xl transition-all flex items-center justify-center gap-2.5 rounded-2xl active:scale-[0.98] disabled:opacity-50 cursor-pointer ${tokens.primaryBtn}`}
              >
                {isWixReAuthing ? (
                  <>
                    <Loader2 className="animate-spin" size={16} />
                    <span>Redirecting to School Portal...</span>
                  </>
                ) : (
                  <>
                    <LogIn size={16} />
                    <span>Sign In to Continue</span>
                  </>
                )}
              </button>

              {/* TOGGLE FOR PASSWORD / ACCESS KEY OPTION */}
              <div className="pt-1 text-center">
                <button
                  type="button"
                  onClick={() => setShowPasswordAuth(prev => !prev)}
                  className="w-full py-2 px-3 text-[11px] font-bold text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white transition-colors flex items-center justify-center gap-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800/50 cursor-pointer"
                >
                  <Key size={14} className={tokens.accentText} />
                  <span>{showPasswordAuth ? 'Hide Password / Access Key option' : 'Use Password or Access Key instead'}</span>
                  <ChevronDown size={14} className={`transition-transform duration-200 ${showPasswordAuth ? 'rotate-180' : ''}`} />
                </button>
              </div>

              {/* SECOND OPTION: Hidden by default, toggled via state */}
              {showPasswordAuth && !reAuth2FAChallenge && (
                <form onSubmit={handleReAuthSubmit} className="space-y-3.5 pt-2 border-t border-slate-200 dark:border-slate-800 animate-fade-in">
                  {!state.user?.email && (
                    <div className="space-y-1 text-left">
                      <label className="text-[9px] font-black text-slate-500 dark:text-slate-400 tracking-widest ml-1 uppercase">Email Address</label>
                      <div className="relative group">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 group-focus-within:text-indigo-500 transition-colors" size={16} />
                        <input 
                          type="email" 
                          required
                          value={reAuthEmail}
                          onChange={e => setReAuthEmail(e.target.value)}
                          className="w-full pl-11 pr-4 py-3 bg-slate-50 dark:bg-slate-950/80 border border-slate-200 dark:border-slate-800 rounded-2xl outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all font-bold text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-600 text-xs" 
                          placeholder="student@example.com"
                        />
                      </div>
                    </div>
                  )}

                  <div className="space-y-1 text-left">
                    <label className="text-[9px] font-black text-slate-500 dark:text-slate-400 tracking-widest ml-1 uppercase">
                      {state.user?.role === 'admin' ? 'Admin Access Code / Password' : 'Password / Access Key'}
                    </label>
                    <div className="relative group">
                      <Key className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 group-focus-within:text-indigo-500 transition-colors" size={16} />
                      <input 
                        type="password" 
                        required
                        value={reAuthPassword}
                        onChange={e => setReAuthPassword(e.target.value)}
                        className="w-full pl-11 pr-4 py-3 bg-slate-50 dark:bg-slate-950/80 border border-slate-200 dark:border-slate-800 rounded-2xl outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all font-bold text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-600 text-xs" 
                        placeholder="••••••••"
                      />
                    </div>
                  </div>

                  <button 
                    type="submit"
                    disabled={isReAuthLoggingIn}
                    className={`w-full py-3.5 font-black text-xs tracking-widest uppercase shadow-md transition-all flex items-center justify-center gap-2 rounded-2xl active:scale-[0.98] disabled:opacity-50 cursor-pointer ${tokens.secondaryBtn}`}
                  >
                    {isReAuthLoggingIn ? (
                      <>
                        <Loader2 className="animate-spin" size={16} />
                        <span>Resuming Session...</span>
                      </>
                    ) : (
                      <>
                        <LogIn size={16} />
                        <span>Resume Study Session</span>
                      </>
                    )}
                  </button>
                </form>
              )}

              {/* 2FA Challenge within Session Expired Modal */}
              {reAuth2FAChallenge && (
                <form onSubmit={handleReAuth2FASubmit} className="space-y-3.5 pt-2 border-t border-slate-200 dark:border-slate-800 animate-fade-in text-left">
                  <div className="p-3 bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800/50 rounded-2xl space-y-1">
                    <p className="text-xs font-black text-indigo-900 dark:text-indigo-200 flex items-center gap-1.5">
                      <ShieldCheck size={14} className="text-indigo-600 dark:text-indigo-400" />
                      Two-Factor Verification Required
                    </p>
                    <p className="text-[10px] text-slate-600 dark:text-slate-400">
                      {reAuthUsingBackup ? 'Enter an emergency backup code.' : 'Enter the 6-digit code from Google Authenticator.'}
                    </p>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[9px] font-black text-slate-500 dark:text-slate-400 tracking-widest ml-1 uppercase">
                      {reAuthUsingBackup ? 'Emergency Backup Code' : 'Verification Code'}
                    </label>
                    <input 
                      type="text" 
                      required
                      autoFocus
                      inputMode={reAuthUsingBackup ? 'text' : 'numeric'}
                      maxLength={reAuthUsingBackup ? 10 : 6}
                      value={reAuth2FACode}
                      onChange={e => {
                        const val = reAuthUsingBackup ? e.target.value.toUpperCase().trim() : e.target.value.replace(/\D/g, '');
                        setReAuth2FACode(val);
                      }}
                      className="w-full text-center tracking-[0.4em] font-mono text-xl font-black py-3 bg-slate-50 dark:bg-slate-950/80 border border-slate-200 dark:border-slate-800 rounded-2xl outline-none text-slate-900 dark:text-white" 
                      placeholder={reAuthUsingBackup ? "ABC12345" : "000000"}
                    />
                  </div>

                  <label className="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-900/50 rounded-xl cursor-pointer">
                    <input 
                      type="checkbox"
                      checked={reAuthTrustDevice}
                      onChange={e => setReAuthTrustDevice(e.target.checked)}
                      className="rounded border-slate-300 text-indigo-600"
                    />
                    <span className="text-[11px] font-bold text-slate-700 dark:text-slate-300 flex items-center gap-1">
                      <Laptop size={12} /> Trust this device for 30 days
                    </span>
                  </label>

                  <button 
                    type="submit"
                    disabled={isReAuthLoggingIn || (!reAuthUsingBackup && reAuth2FACode.length !== 6) || (reAuthUsingBackup && reAuth2FACode.length < 6)}
                    className={`w-full py-3.5 font-black text-xs tracking-widest uppercase shadow-md transition-all flex items-center justify-center gap-2 rounded-2xl active:scale-[0.98] disabled:opacity-50 cursor-pointer ${tokens.primaryBtn}`}
                  >
                    {isReAuthLoggingIn ? <Loader2 className="animate-spin" size={16} /> : <ShieldCheck size={16} />}
                    <span>Verify & Continue</span>
                  </button>

                  <div className="text-center pt-1">
                    <button
                      type="button"
                      onClick={() => {
                        setReAuthUsingBackup(!reAuthUsingBackup);
                        setReAuth2FACode('');
                      }}
                      className="text-[10px] font-bold text-indigo-500 hover:underline"
                    >
                      {reAuthUsingBackup ? 'Use Google Authenticator code' : 'Use emergency backup code'}
                    </button>
                  </div>
                </form>
              )}

              {/* Log Out Option */}
              <div className="pt-2 border-t border-slate-200/60 dark:border-slate-800/60">
                <button 
                  type="button"
                  onClick={async () => {
                    setIsSessionExpired(false);
                    await logout();
                  }}
                  className="w-full py-2.5 hover:bg-slate-100 dark:hover:bg-slate-800/40 text-[10px] text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white rounded-xl font-bold tracking-widest uppercase transition-all flex items-center justify-center gap-2 cursor-pointer"
                >
                  <LogOut size={14} />
                  <span>Cancel & Sign Out</span>
                </button>
              </div>

            </div>
            
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};

export const AuthGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const tokens = useThemeTokens();
  const { isAuthenticated, isBlocked, isLoading, login, verify2FA, error, activeCert } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [certs, setCerts] = useState<CertificationType[]>(StorageService.getCachedCertifications());
  const [userDismissedStaffLogin, setUserDismissedStaffLogin] = useState(false);
  const [showStaffLogin, setShowStaffLogin] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      const href = (window.location.href || '').toLowerCase();
      const hash = (window.location.hash || '').toLowerCase();
      const search = (window.location.search || '').toLowerCase();
      const pathname = (window.location.pathname || '').toLowerCase();

      if (
        href.includes('role=manage') ||
        href.includes('role=admin') ||
        href.includes('role=superadmin') ||
        hash.includes('role=manage') ||
        search.includes('role=manage') ||
        hash.startsWith('#/manage') ||
        pathname.startsWith('/manage')
      ) {
        try { sessionStorage.setItem('dns_manage_mode', 'true'); } catch (e) {}
        return true;
      }
      try {
        if (sessionStorage.getItem('dns_manage_mode') === 'true') {
          return true;
        }
      } catch (e) {}
    }
    return false;
  });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  // 2FA Challenge State for Staff / Super Admin Login
  const [twoFactorChallenge, setTwoFactorChallenge] = useState<{
    preAuthToken: string;
    user: { id: string; email: string; name?: string; role?: string };
  } | null>(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(true);
  const [twoFactorError, setTwoFactorError] = useState<string | null>(null);
  const [isVerifying2FA, setIsVerifying2FA] = useState(false);
  const [isUsingBackupCode, setIsUsingBackupCode] = useState(false);

  useEffect(() => {
    StorageService.getCertifications().then(data => {
      setCerts(data);
    }).catch(console.warn);

    const handleCertUpdate = () => {
      setCerts(StorageService.getCachedCertifications());
    };

    window.addEventListener('certifications-updated', handleCertUpdate);
    return () => window.removeEventListener('certifications-updated', handleCertUpdate);
  }, []);

  const adConfig = SponsoredService.getConfig();
  const isGlobalGuestTrialAllowed = adConfig.isGuestTrialEnabled !== false;

  const isPublicFaq = useMemo(() => {
    return location.pathname === '/faq' || location.pathname.endsWith('/faq') || location.pathname.includes('/faq/');
  }, [location.pathname]);

  const isPublicProgramRoute = useMemo(() => {
    let p = location.pathname.toLowerCase();
    if (p.startsWith('/practice-exams')) {
      p = p.slice('/practice-exams'.length);
    }
    if (!p.startsWith('/')) {
      p = '/' + p;
    }

    const cleanPath = (typeof window !== 'undefined' ? window.location.pathname.toLowerCase() : '');
    const hash = (typeof window !== 'undefined' ? window.location.hash.toLowerCase() : '');

    return (
      p.startsWith('/program/') ||
      p.startsWith('/programs/') ||
      p.startsWith('/certifications/') ||
      p === '/program' ||
      p === '/programs' ||
      p === '/certifications' ||
      p.endsWith('/overview') ||
      p.endsWith('/details') ||
      p.endsWith('/program') ||
      p.endsWith('/programs') ||
      cleanPath.startsWith('/program/') ||
      cleanPath.startsWith('/programs/') ||
      cleanPath.startsWith('/certifications/') ||
      cleanPath.includes('/program/') ||
      cleanPath.includes('/programs/') ||
      cleanPath.includes('/certifications/') ||
      cleanPath.endsWith('/overview') ||
      cleanPath.endsWith('/details') ||
      hash.startsWith('#/program/') ||
      hash.startsWith('#/programs/') ||
      hash.startsWith('#/certifications/') ||
      hash.includes('/program/') ||
      hash.includes('/programs/') ||
      hash.includes('/certifications/') ||
      hash.endsWith('/overview') ||
      hash.endsWith('/details') ||
      hash.endsWith('/program') ||
      hash.endsWith('/programs')
    );
  }, [location.pathname]);

  // Determine if current route is allowed for guest access based on certification config
  const isGuestAccessibleRoute = useMemo(() => {
    if (!isGlobalGuestTrialAllowed) return false;

    let path = location.pathname.toLowerCase();
    if (path.startsWith('/practice-exams')) {
      path = path.slice('/practice-exams'.length);
    }
    if (path.startsWith('/practice')) {
      path = path.slice('/practice'.length);
    }
    if (!path.startsWith('/')) {
      path = '/' + path;
    }

    let hashPath = '';
    if (typeof window !== 'undefined' && window.location.hash) {
      hashPath = window.location.hash.toLowerCase().replace(/^#\/?/, '/').split('?')[0];
      if (hashPath.startsWith('/practice-exams')) {
        hashPath = hashPath.slice('/practice-exams'.length);
      }
      if (hashPath.startsWith('/practice')) {
        hashPath = hashPath.slice('/practice'.length);
      }
      if (!hashPath.startsWith('/')) {
        hashPath = '/' + hashPath;
      }
    }
    
    // Choose active path (prefer route pathname if not empty/root, else fallback to hashPath)
    const effectivePath = (path !== '/' && path !== '') ? path : (hashPath || path);

    // Top-level aliases, public gateway landing page, content layer & error pages
    if (effectivePath === '/' || effectivePath === '/gateway' || effectivePath.startsWith('/gateway')) return true;
    if (effectivePath === '/study-quiz' || effectivePath.startsWith('/study-quiz')) return true;
    if (effectivePath === '/faq' || effectivePath.startsWith('/faq')) return true;
    if (effectivePath === '/programs' || effectivePath.startsWith('/programs') || effectivePath === '/program' || effectivePath.startsWith('/program') || effectivePath.startsWith('/certifications')) return true;
    if (effectivePath === '/404') return true;
    if (effectivePath === '/cookie-consent' || effectivePath.startsWith('/cookie-consent') || effectivePath === '/cookies' || effectivePath.startsWith('/cookies') || effectivePath.startsWith('/compliance/cookies') || effectivePath.startsWith('/compliance/cookie-policy')) return true;

    // Path pattern: /:certId/:route (e.g., /cna/flashcards, /cna/study-quiz, /ekg/guide, /phlebotomy/practice)
    const segments = effectivePath.split('/').filter(Boolean);
    if (segments.length === 0) return false;

    // Check if first segment matches an admin route
    if (segments[0] === 'manage') return false;

    const certIdCandidate = segments[0];
    const subRoute = segments[1] || 'dashboard';

    // Public details/overview or programs root
    if (subRoute === 'overview' || subRoute === 'details' || subRoute === 'program' || subRoute === 'programs') return true;
    if (certIdCandidate === 'programs' || certIdCandidate === 'program' || certIdCandidate === 'certifications') return true;

    // Dashboard requires authentication and is never a guest trial route
    if (subRoute === 'dashboard') return false;

    // Find cert from registry
    const matchedCert = certs.find(c => c.id.toLowerCase() === certIdCandidate);

    if (matchedCert) {
      // If guest trial is explicitly disabled for this cert
      if (matchedCert.isGuestTrialEnabled === false) {
        return false;
      }

      // Check allowed routes from cert config (ensure standard guest routes like exam, practice, flashcards, guide are open)
      const allowedRoutes = (matchedCert.guestAllowedRoutes && matchedCert.guestAllowedRoutes.length > 0)
        ? Array.from(new Set([...matchedCert.guestAllowedRoutes, 'exam', 'practice', 'flashcards', 'study-quiz', 'guide']))
        : ['flashcards', 'practice', 'study-quiz', 'exam', 'guide'];

      const isSubRouteAllowed = allowedRoutes.some(r => {
        const cleanR = r.toLowerCase().trim().replace(/^\/+/, '');
        return subRoute === cleanR || effectivePath.includes(`/${cleanR}`);
      });

      if (isSubRouteAllowed) return true;

      // Check custom guest routes
      if (matchedCert.customGuestRoutes && matchedCert.customGuestRoutes.length > 0) {
        const isCustomAllowed = matchedCert.customGuestRoutes.some(cr => {
          if (!cr) return false;
          let cleanCr = cr.toLowerCase().trim().replace(/^\/+/, '');
          const certPrefix = matchedCert.id.toLowerCase() + '/';
          if (cleanCr.startsWith(certPrefix)) {
            cleanCr = cleanCr.slice(certPrefix.length);
          }
          return subRoute === cleanCr || effectivePath.includes(`/${cleanCr}`) || effectivePath.endsWith(cleanCr);
        });
        if (isCustomAllowed) return true;
      }

      return false;
    }

    // Default fallback if certs list is hydrating or certId is 'cna' or dynamic cert
    const defaultAllowed = ['flashcards', 'practice', 'study-quiz', 'exam', 'guide'];
    return defaultAllowed.some(r => effectivePath.includes(`/${r}`) || effectivePath === `/${r}` || subRoute === r);
  }, [location.pathname, isGlobalGuestTrialAllowed, certs]);

  const isManageMode = useMemo(() => {
    // 1. React Router search
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('role') === 'manage' || searchParams.get('manage') === 'true' || searchParams.get('role') === 'admin' || searchParams.get('role') === 'superadmin') {
      try { sessionStorage.setItem('dns_manage_mode', 'true'); } catch (e) {}
      return true;
    }

    // 2. React Router hash query (e.g. #/cna/dashboard?role=manage)
    if (location.hash && location.hash.includes('?')) {
      const hashQuery = location.hash.slice(location.hash.indexOf('?'));
      const hashParams = new URLSearchParams(hashQuery);
      if (hashParams.get('role') === 'manage' || hashParams.get('manage') === 'true' || hashParams.get('role') === 'admin' || hashParams.get('role') === 'superadmin') {
        try { sessionStorage.setItem('dns_manage_mode', 'true'); } catch (e) {}
        return true;
      }
    }

    if (typeof window !== 'undefined') {
      // 3. Browser window.location.search
      const winSearchParams = new URLSearchParams(window.location.search);
      if (winSearchParams.get('role') === 'manage' || winSearchParams.get('manage') === 'true' || winSearchParams.get('role') === 'admin' || winSearchParams.get('role') === 'superadmin') {
        try { sessionStorage.setItem('dns_manage_mode', 'true'); } catch (e) {}
        return true;
      }

      // 4. Browser window.location.hash query
      const rawHash = window.location.hash || '';
      if (rawHash.includes('?')) {
        const winHashQuery = rawHash.slice(rawHash.indexOf('?'));
        const winHashParams = new URLSearchParams(winHashQuery);
        if (winHashParams.get('role') === 'manage' || winHashParams.get('manage') === 'true' || winHashParams.get('role') === 'admin' || winHashParams.get('role') === 'superadmin') {
          try { sessionStorage.setItem('dns_manage_mode', 'true'); } catch (e) {}
          return true;
        }
      }

      // 5. Hash path like #/manage
      if (rawHash.startsWith('#/manage') || rawHash.includes('/manage')) {
        try { sessionStorage.setItem('dns_manage_mode', 'true'); } catch (e) {}
        return true;
      }

      // 6. Full href string
      const href = window.location.href.toLowerCase();
      if (href.includes('role=manage') || href.includes('role=admin') || href.includes('role=superadmin')) {
        try { sessionStorage.setItem('dns_manage_mode', 'true'); } catch (e) {}
        return true;
      }

      // 7. sessionStorage persistence
      try {
        if (sessionStorage.getItem('dns_manage_mode') === 'true') {
          return true;
        }
      } catch (e) {}
    }

    // 8. location.pathname
    if (location.pathname.startsWith('/manage')) {
      try { sessionStorage.setItem('dns_manage_mode', 'true'); } catch (e) {}
      return true;
    }

    return false;
  }, [location.search, location.hash, location.pathname]);

  // Keep staff login active when manage mode is enabled, unless explicitly dismissed
  useEffect(() => {
    if (isManageMode && !userDismissedStaffLogin && !showStaffLogin) {
      setShowStaffLogin(true);
    }
  }, [isManageMode, userDismissedStaffLogin, showStaffLogin]);

  const handleStaffSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setIsLoggingIn(true);
      setTwoFactorError(null);
      try {
          const res = await login(email, password);
          if (res && res.require2FA) {
              setTwoFactorChallenge({ preAuthToken: res.preAuthToken, user: res.user });
              setTwoFactorCode('');
          }
      } catch (err) {} finally {
          setIsLoggingIn(false);
      }
  };

  const handle2FASubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!twoFactorChallenge || !twoFactorCode) return;
      setIsVerifying2FA(true);
      setTwoFactorError(null);
      try {
          await verify2FA(twoFactorChallenge.preAuthToken, twoFactorCode.trim(), trustDevice);
          setTwoFactorChallenge(null);
          setTwoFactorCode('');
      } catch (err: any) {
          setTwoFactorError(err.message || 'Invalid verification code. Please try again.');
      } finally {
          setIsVerifying2FA(false);
      }
  };

  const handleLoginRedirect = () => {
      setIsRedirecting(true);
      const currentFullUrl = window.location.href;
      const loginRedirectUrl = `https://www.diamondnursingschool.com/studyhub-login?redirect=${encodeURIComponent(currentFullUrl)}`;
      window.location.href = loginRedirectUrl;
  };

  const isGatewayRoute = useMemo(() => {
    let p = location.pathname.toLowerCase();
    if (p.startsWith('/practice-exams')) p = p.slice('/practice-exams'.length);
    if (!p.startsWith('/')) p = '/' + p;
    return p === '/' || p === '/gateway' || p.startsWith('/gateway');
  }, [location.pathname]);

  if (isPublicFaq || isPublicProgramRoute || isGatewayRoute) {
    return <>{children}</>;
  }

  if (isGuestAccessibleRoute && !isAuthenticated && !isBlocked) {
    return <>{children}</>;
  }

  if (isLoading) return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-slate-900">
      <div className="relative w-24 h-24 mb-8">
        <div className="absolute inset-0 border-4 border-medical-500/20 rounded-3xl"></div>
        <div className="absolute inset-0 border-4 border-medical-500 border-t-transparent rounded-3xl animate-spin"></div>
        <div className="absolute inset-0 flex items-center justify-center text-medical-500">
            <Zap size={32} />
        </div>
      </div>
      <p className="text-[10px] font-black text-slate-500 tracking-[0.4em]">Verifying Clinical Credentials...</p>
    </div>
  );

  if (isAuthenticated && isBlocked) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-slate-950 p-6 text-center">
          <Lock className="w-16 h-16 text-rose-500 mx-auto mb-6 shadow-2xl" />
          <h2 className="text-2xl font-black text-white tracking-tight">Access Restricted</h2>
          <p className="text-slate-400 mt-2 font-medium">"Registry access has been suspended for this identity."</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col">
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
            <div className="max-w-xl w-full space-y-12 animate-fade-in px-4">
            {!showStaffLogin ? (
                <>
                <div className="w-40 h-40 md:w-72 md:h-72 flex items-center justify-center mx-auto transition-transform hover:scale-105 active:scale-95 duration-500">
                    <img 
                    src="https://static.wixstatic.com/shapes/cc4206_481d89df948342ab9d490c0c0ad92950.svg" 
                    alt="Diamond School of Nursing" 
                    className="w-full h-full object-contain filter drop-shadow-2xl"
                    />
                </div>
                <div className="space-y-4">
                    <h2 className="text-3xl md:text-5xl font-black text-white tracking-tighter leading-none">Authentication Required</h2>
                    <p className="text-slate-400 text-sm md:text-base font-medium leading-relaxed max-w-md mx-auto">
                        "Please initiate your study session."
                    </p>
                </div>
                <div className="space-y-6 max-w-sm mx-auto">
                    <button 
                    onClick={handleLoginRedirect}
                    disabled={isRedirecting}
                    className="w-full py-5 md:py-6 bg-white text-slate-950 rounded-[1.5rem] md:rounded-[2rem] font-black text-sm md:text-base tracking-widest hover:bg-emerald-50 transition-all active:scale-95 shadow-[0_20px_50px_rgba(255,255,255,0.15)] border-b-4 border-slate-200 flex items-center justify-center gap-3 disabled:opacity-50"
                    >
                        {isRedirecting ? (
                            <>
                                <Loader2 className="animate-spin" size={20} />
                                Verifying...
                            </>
                        ) : (
                            "Register / Sign In"
                        )}
                    </button>

                    {adConfig.isGuestTrialEnabled !== false && (
                        <div className="pt-2 space-y-3">
                            <div className="flex items-center gap-2">
                                <div className="h-px bg-slate-800 flex-1"></div>
                                <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">No Account Needed</span>
                                <div className="h-px bg-slate-800 flex-1"></div>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                                <button
                                    type="button"
                                    onClick={() => navigate(`/${activeCert?.id || 'cna'}/practice`)}
                                    className="py-3 px-2 bg-slate-900 hover:bg-slate-800 text-teal-400 hover:text-teal-300 border border-teal-500/30 rounded-2xl font-black text-[10px] uppercase tracking-wider flex items-center justify-center gap-1 transition-all shadow-md active:scale-95 text-center cursor-pointer"
                                >
                                    <Zap size={13} /> Quiz
                                </button>
                                <button
                                    type="button"
                                    onClick={() => navigate(`/${activeCert?.id || 'cna'}/flashcards`)}
                                    className="py-3 px-2 bg-slate-900 hover:bg-slate-800 text-amber-400 hover:text-amber-300 border border-amber-500/30 rounded-2xl font-black text-[10px] uppercase tracking-wider flex items-center justify-center gap-1 transition-all shadow-md active:scale-95 text-center cursor-pointer"
                                >
                                    <Zap size={13} /> Cards
                                </button>
                                <button
                                    type="button"
                                    onClick={() => navigate(`/${activeCert?.id || 'cna'}/exam`)}
                                    className="py-3 px-2 bg-slate-900 hover:bg-slate-800 text-indigo-400 hover:text-indigo-300 border border-indigo-500/30 rounded-2xl font-black text-[10px] uppercase tracking-wider flex items-center justify-center gap-1 transition-all shadow-md active:scale-95 text-center cursor-pointer"
                                >
                                    <Zap size={13} /> Free Exam
                                </button>
                            </div>
                            <button
                                type="button"
                                onClick={() => navigate(`/program/${activeCert?.id || 'cna'}`)}
                                className="w-full py-2.5 px-3 bg-slate-900/90 hover:bg-slate-800 text-sky-300 hover:text-white border border-sky-500/30 rounded-xl font-black text-[10px] uppercase tracking-wider flex items-center justify-center gap-2 transition-all shadow-sm active:scale-95 text-center cursor-pointer"
                            >
                                <span>{activeCert?.shortName || 'Certification'} Program Guide & Blueprint</span>
                            </button>
                            <p className="text-[10px] text-slate-400 font-medium text-center">
                                Try quizzes, flashcards, candidate handbooks, and practice exams free with no registration required.
                            </p>
                        </div>
                    )}

                    {isManageMode && (
                      <div className="pt-3 space-y-2 border-t border-slate-800/80 animate-fade-in">
                        <button 
                            type="button"
                            onClick={() => {
                              setShowStaffLogin(true);
                              setUserDismissedStaffLogin(false);
                            }}
                            className={`w-full py-4 px-4 ${tokens.primaryBtn} rounded-2xl font-black text-xs tracking-widest uppercase transition-all shadow-xl active:scale-95 flex items-center justify-center gap-2.5 cursor-pointer`}
                        >
                            <ShieldCheck size={18} className="shrink-0" />
                            <span>Super Admin & Staff Portal Login</span>
                        </button>
                        <p className="text-[10px] text-slate-400 font-medium text-center">
                          Administrative role requested. Click above to authenticate to the Clinical Registry Management Console.
                        </p>
                      </div>
                    )}
                </div>
                </>
            ) : twoFactorChallenge ? (
                <div className={`bg-white rounded-[2.5rem] p-8 md:p-10 shadow-2xl text-left space-y-6 border-b-8 ${tokens.accentBorder} animate-pop max-w-md mx-auto`}>
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-2xl ${tokens.badge} flex items-center justify-center`}>
                                <ShieldCheck size={22} className={tokens.accentText} />
                            </div>
                            <div>
                                <h2 className="text-xl font-black text-slate-900 tracking-tight">Two-Factor Verification</h2>
                                <p className="text-[10px] font-black text-slate-400 tracking-widest uppercase">Google Authenticator Required</p>
                            </div>
                        </div>
                        <button 
                            onClick={() => {
                                setTwoFactorChallenge(null);
                                setTwoFactorCode('');
                                setTwoFactorError(null);
                            }} 
                            className="p-2 text-slate-300 hover:text-rose-500 rounded-xl transition-colors cursor-pointer"
                        >
                            <X size={20}/>
                        </button>
                    </div>

                    <div className="p-3.5 bg-slate-50 border border-slate-200/80 rounded-2xl text-xs text-slate-600 space-y-0.5">
                        <p className="font-bold text-slate-900">Admin: {twoFactorChallenge.user.email}</p>
                        <p className="text-[11px] text-slate-500 leading-relaxed">
                            {isUsingBackupCode ? 'Enter one of your 8-character emergency backup recovery codes.' : 'Open Google Authenticator on your mobile phone and enter the 6-digit security code.'}
                        </p>
                    </div>

                    {twoFactorError && (
                        <div className="p-4 bg-rose-50 border border-rose-100 rounded-2xl flex items-center gap-3 text-rose-600 animate-pop">
                            <AlertTriangle size={18} className="shrink-0" />
                            <p className="text-[11px] font-bold">{twoFactorError}</p>
                        </div>
                    )}

                    <form onSubmit={handle2FASubmit} className="space-y-5">
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-slate-400 tracking-widest uppercase ml-1">
                                {isUsingBackupCode ? 'Emergency Backup Code' : '6-Digit Security Code'}
                            </label>
                            <input 
                                type="text" 
                                required
                                autoFocus
                                inputMode={isUsingBackupCode ? 'text' : 'numeric'}
                                autoComplete="one-time-code"
                                maxLength={isUsingBackupCode ? 10 : 6}
                                value={twoFactorCode}
                                onChange={e => {
                                    const val = isUsingBackupCode ? e.target.value.toUpperCase().trim() : e.target.value.replace(/\D/g, '');
                                    setTwoFactorCode(val);
                                }}
                                className="w-full text-center tracking-[0.4em] font-mono text-2xl font-black py-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all text-slate-900 placeholder-slate-300" 
                                placeholder={isUsingBackupCode ? "ABC12345" : "000000"}
                            />
                        </div>

                        {/* Trust this device checkbox */}
                        <label className="flex items-start gap-3 p-3 bg-slate-50 hover:bg-slate-100/80 border border-slate-200/80 rounded-2xl cursor-pointer transition-colors">
                            <input 
                                type="checkbox" 
                                checked={trustDevice}
                                onChange={e => setTrustDevice(e.target.checked)}
                                className="mt-0.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                            />
                            <div className="text-left space-y-0.5">
                                <span className="text-xs font-black text-slate-800 flex items-center gap-1.5">
                                    <Laptop size={14} className="text-indigo-600" />
                                    Trust this device for 30 days
                                </span>
                                <p className="text-[10px] text-slate-500 font-medium leading-tight">
                                    Bypasses verification on this browser for 30 days. Untrusted devices will always require a 2FA code.
                                </p>
                            </div>
                        </label>

                        <button 
                            type="submit"
                            disabled={isVerifying2FA || (!isUsingBackupCode && twoFactorCode.length !== 6) || (isUsingBackupCode && twoFactorCode.length < 6)}
                            className="w-full py-4 bg-slate-900 text-white rounded-2xl font-black text-xs tracking-widest shadow-xl hover:bg-black transition-all flex items-center justify-center gap-3 border-b-4 border-black disabled:opacity-50 cursor-pointer"
                        >
                            {isVerifying2FA ? <Loader2 className="animate-spin" size={18} /> : <ShieldCheck size={18} />}
                            Verify & Enter Admin Console
                        </button>
                    </form>

                    <div className="flex flex-col gap-2 pt-2 border-t border-slate-100 text-center">
                        <button
                            type="button"
                            onClick={() => {
                                setIsUsingBackupCode(!isUsingBackupCode);
                                setTwoFactorCode('');
                                setTwoFactorError(null);
                            }}
                            className="text-[11px] font-bold text-indigo-600 hover:text-indigo-800 transition-colors cursor-pointer"
                        >
                            {isUsingBackupCode ? 'Use Google Authenticator app instead' : 'Lost your phone? Use an emergency backup code'}
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setTwoFactorChallenge(null);
                                setTwoFactorCode('');
                                setTwoFactorError(null);
                            }}
                            className="text-[10px] font-bold text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
                        >
                            Cancel and return to password sign in
                        </button>
                    </div>
                </div>
            ) : (
                <div className={`bg-white rounded-[2.5rem] p-10 shadow-2xl text-left space-y-8 border-b-8 ${tokens.accentBorder} animate-pop max-w-md mx-auto`}>
                    <div className="flex justify-between items-center">
                        <div>
                            <h2 className={`text-2xl font-black text-slate-900 tracking-tight ${tokens.headerFont}`}>Super Admin & Staff Portal</h2>
                            <p className="text-[10px] font-black text-slate-400 tracking-widest uppercase">Administrative Registry Access</p>
                        </div>
                        <button 
                            onClick={() => {
                                setShowStaffLogin(false);
                                setUserDismissedStaffLogin(true);
                            }} 
                            className="p-2 text-slate-400 hover:text-rose-500 rounded-xl transition-colors cursor-pointer"
                            title="Close Super Admin Login"
                        >
                            <X size={20}/>
                        </button>
                    </div>

                    {error && (
                        <div className="p-4 bg-rose-50 border border-rose-100 rounded-2xl flex items-center gap-3 text-rose-600 animate-pop">
                            <AlertTriangle size={18} />
                            <p className="text-[10px] font-black">{error}</p>
                        </div>
                    )}

                    <form onSubmit={handleStaffSubmit} className="space-y-6">
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Registry Email</label>
                            <div className="relative group">
                                <Mail className={`absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:${tokens.accentText} transition-colors`} size={18} />
                                <input 
                                    type="email" 
                                    required
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:bg-white focus:ring-4 focus:ring-indigo-500/5 transition-all font-bold" 
                                    placeholder="root@namespace.admin"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Access Code</label>
                            <div className="relative group">
                                <Key className={`absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:${tokens.accentText} transition-colors`} size={18} />
                                <input 
                                    type="password" 
                                    required
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:bg-white focus:ring-4 focus:ring-indigo-500/5 transition-all font-bold" 
                                    placeholder="••••••••"
                                />
                            </div>
                        </div>
                        <button 
                            type="submit"
                            disabled={isLoggingIn}
                            className={`w-full py-5 ${tokens.primaryBtn} rounded-2xl font-black text-xs tracking-widest shadow-xl transition-all flex items-center justify-center gap-3 disabled:opacity-50`}
                        >
                            {isLoggingIn ? <Loader2 className="animate-spin" size={18} /> : <LogIn size={18} />}
                            Authenticate Session
                        </button>
                    </form>
                    <div className="text-center">
                        <p className="text-[8px] font-bold text-slate-400 tracking-widest leading-relaxed">
                            This registry is monitored. Unauthorized access attempts are indexed and reported to the security partition.
                        </p>
                    </div>
                </div>
            )}
            </div>
        </div>
        <Footer config={adConfig} />
      </div>
    );
  }

  return <>{children}</>;
};
